import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

import { app, functions as functionsInstance } from "../config/firebase";
import { EmployeeI } from "@/pages/Employees";
import { useAuth } from "./AuthContext";

interface UsersContextProps {
    users: EmployeeI[];
    loading: boolean;
    error: string | null;
    fetchUsers: () => Promise<void>;
    changeRole: (data: { uid: string; role: string }) => Promise<void>;
    deleteUser: (uid: string) => Promise<void>;
}

const UsersContext = createContext<UsersContextProps>({
    users: [],
    loading: false,
    error: null,
    fetchUsers: async () => {},
    changeRole: async () => {},
    deleteUser: async () => {},
});

export const UsersProvider = ({ children }: { children: React.ReactNode }) => {
    const [users, setUsers] = useState<EmployeeI[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { role, user } = useAuth();

    // ВАЖНО: регион должен совпадать с деплоем твоих функций
    // если функции в другом регионе, поменяй "us-central1"
    const regionalFunctions = getFunctions(app, "us-central1");

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const listUsersFn = httpsCallable(regionalFunctions, "listUsers");
            const result = await listUsersFn({});

            const payload = result.data as any;
            const arr = payload?.users;

            if (!Array.isArray(arr)) {
                setUsers([]);
                setError("Неверный формат данных от сервера");
                return;
            }

            setUsers(arr);
        } catch (err: any) {
            console.error("fetchUsers error:", err);
            setUsers([]);
            setError(err?.message || "Ошибка при получении списка пользователей");
        } finally {
            setLoading(false);
        }
    }, [regionalFunctions]);

    const changeRole = useCallback(
        async (data: { uid: string; role: string }) => {
            try {
                setLoading(true);
                setError(null);

                const changeUserRole = httpsCallable(functionsInstance, "updateUserRole");
                await changeUserRole(data);

                await fetchUsers();
            } catch (err: any) {
                console.error("changeRole error:", err);
                setError(err?.message || "Ошибка изменения роли");
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [fetchUsers]
    );

    const deleteUser = useCallback(
        async (uid: string) => {
            try {
                setLoading(true);
                setError(null);

                const deleteUserCallable = httpsCallable(functionsInstance, "deleteUser");
                await deleteUserCallable({ uid });

                await fetchUsers();
            } catch (err: any) {
                console.error("deleteUser error:", err);
                setError(err?.message || "Ошибка удаления пользователя");
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [fetchUsers]
    );

    useEffect(() => {
        // если не залогинен, чистим
        if (!user) {
            setUsers([]);
            return;
        }

        // ПОКАЗЫВАЕМ ДЛЯ admin (потом заменишь на superadmin)
        if (role === "superadmin") {
            fetchUsers();
            return;
        }

        // если не admin, список не грузим (чтобы не ловить permission denied)
        setUsers([]);
    }, [user, role, fetchUsers]);

    return (
        <UsersContext.Provider value={{ users, loading, error, fetchUsers, changeRole, deleteUser }}>
            {children}
        </UsersContext.Provider>
    );
};

export const useUsers = () => useContext(UsersContext);
